import React, { useContext, useLayoutEffect, useRef } from 'react'
import {
  arrayOf,
  exact,
  oneOf,
  bool,
  shape,
  string,
  number,
  oneOfType,
} from 'prop-types'
import * as Styled from './IndicatorTable.styles'
import { isRequiredIf } from 'settings/propTypeDefinitions'

// component stack
import { TextContext } from 'hocs/TextContextProvider/TextContextProvider'
import { IndicatorIcon, QsrHeader } from '../'
import { Button, Tooltip, DataTable } from 'components/atoms'

/**
 * table component
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const IndicatorTable = (props) => {
  const {
    type,
    data,
    treatmentCases,
    isTreatmentReason,
    tooltip,
    description,
    tableIndex,
  } = props
  const textContext = useContext(TextContext)
  const footerRef = useRef(null)
  const [showDetails, setShowDetails] = React.useState(null)
  const renderIndicatorTable =
    data.table?.length > 0 &&
    (type === 'iqtig' ||
      (type === 'qsr' && (data.status === 0 || data.status === 2)))

  useLayoutEffect(() => {
    // scroll to toggle button position on close details explicit 'false' check
    // because initially the value is null (to prevent scrolling on page load)
    if (showDetails === false && footerRef.current) {
      const scrollPos = (footerRef.current.offsetTop - window.innerHeight) / 2
      window.scrollTo({ top: scrollPos })
    }
  }, [showDetails])

  const getExplanationText = (explanation, rating) => {
    if (showDetails) {
      if (explanation[rating].textDetailed !== undefined) {
        return explanation[rating].textDetailed
      }
    }
    return explanation[rating].text
  }

  return (
    <Styled.IndicatorTable>
      {/* only qsr data will have a qsr header */}
      {'qsr' === type && (
        <QsrHeader
          data={ data }
          treatmentCases={ treatmentCases }
          tooltip={ tooltip }
          showDetails={ showDetails }
          showHeadline={ isTreatmentReason }
        />
      )}

      {'iqtig' === type && isTreatmentReason && (
        <div className='headline-container'>
          {tableIndex === 0 && (
            <div className='headline'>
              <span>{textContext['quality.headline.iqtig']}</span>
              <Tooltip
                arrow='left'
                className='tooltip'
                dialogTitle={ tooltip.title }
                infoIconId={ `info-icon` }
                tooltipId={ `tooltip` }>
                {tooltip.tip}
              </Tooltip>
            </div>
          )}

          {data.headline && (
            <div className='sub-headline'>
              <span>{textContext['indicatorTable.qsr.header']}</span>
              <span>»{data.headline}«</span>
            </div>
          )}
        </div>
      )}

      {/* iqtig render table always / qsr only on status 0 || 2 */}
      {renderIndicatorTable && (
        <div className='indicator-table'>
          <DataTable>
            <thead>
              <tr>
                <th>{textContext['indicatorTable.headline.indicator']}</th>
                <th>{textContext['indicatorTable.headline.result']}</th>

                {'iqtig' === type && showDetails && (
                  <th className='referenceRange-column'>
                    {textContext['indicatorTable.headline.referenceRange']}
                  </th>
                )}

                <th>{textContext['indicatorTable.headline.explanation']}</th>
              </tr>
            </thead>
            <tbody>
              {data.table.map((dataRow, index) => {
                if ('qsr' === type && index === 0) {
                  return true
                }

                const { tooltip, result, explanation } = dataRow
                return (
                  <tr key={ index }>
                    <td className='indicator-column'>
                      <span>{dataRow.indicator}</span>
                      {'qsr' === type && tooltip.tip && (
                        <Tooltip
                          className='tooltip'
                          arrow='left'
                          dialogTitle={ tooltip.title }
                          infoIconId={ `info-icon-${ index }` }
                          tooltipId={ `tooltip-${ index }` }>
                          {tooltip.tip}
                        </Tooltip>
                      )}
                    </td>
                    <td className='result-column'>
                      <div className='cell-column'>
                        <IndicatorIcon type={ type } value={ result.rating } />
                        {showDetails && (
                          <>
                            <span>{result.value}</span>
                            <span>{result.range}</span>
                          </>
                        )}
                      </div>
                    </td>
                    {'iqtig' === type && showDetails && (
                      <td className='referenceRange-column'>
                        <div className='cell-column'>
                          <span>{result.referenceRange}</span>
                          <span>&Oslash;: {result.federalAverage}</span>
                        </div>
                      </td>
                    )}
                    <td className='explanation-column'>
                      <span className='title'>
                        {explanation[result.rating].title}
                      </span>
                      <br />
                      <span>
                        {getExplanationText(explanation, result.rating)}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
          <div
            className={ `table-footer ${ 'iqtig' === type ? 'flex-end ' : null }` }
            ref={ footerRef }>
            {'qsr' === type && (
              <div className='treatmentCases-container'>
                <div className='treatmentCases'> {treatmentCases} </div>
                {data.treatmentCasesTooltip && (
                  <Tooltip
                    arrow='left'
                    className='tooltip'
                    dialogTitle={ textContext['tooltip.Title.treatmentCases'] }
                    infoIconId={ `info-icon-${ type }` }
                    tooltipId={ `tooltip-${ type }` }>
                    {data.treatmentCasesTooltip}
                  </Tooltip>
                )}
              </div>
            )}
            <Button
              id='default-btn'
              text={
                !showDetails
                  ? textContext['indicatorTable.details.open']
                  : textContext['indicatorTable.details.closed']
              }
              onClick={ () => setShowDetails(!showDetails) }
              disabled={ false }
              className='btn-link'
            />
          </div>
          {description && <p className='description'>{description}</p>}
        </div>
      )}
    </Styled.IndicatorTable>
  )
}

IndicatorTable.defaultProps = {
  treatmentCases: false,
  isTreatmentReason: false,
  tooltip: false,
}

IndicatorTable.propTypes = {
  type: oneOf(['qsr', 'iqtig']).isRequired,
  tableIndex: number,
  data: shape({
    status: oneOf([0, 1, 2]),
    table: arrayOf(
      shape({
        indicator: string.isRequired,
        tooltip: exact({
          title: string.isRequired,
          tip: string.isRequired,
        }).isRequired,
        result: exact({
          rating: string.isRequired,
          value: oneOfType([string, number]),
          range: string.isRequired,
          referenceRange: isRequiredIf(
            string,
            (props) => 'iqtig' === props.type
          ),
          federalAverage: oneOfType([number, string]),
        }).isRequired,
        explanation: oneOfType([
          /** qsr tables **/
          shape({
            low: shape({
              title: string.isRequired,
              text: string.isRequired,
              textDetailed: string,
            }),
            average: shape({
              title: string.isRequired,
              text: string.isRequired,
              textDetailed: string,
            }),
            high: shape({
              title: string.isRequired,
              text: string.isRequired,
              textDetailed: string,
            }),
          }),
          /** iqtig **/
          shape({
            noRating: shape({
              title: string.isRequired,
              text: string.isRequired,
            }),
            goodRating: shape({
              title: string.isRequired,
              text: string.isRequired,
            }),
          }),
        ]).isRequired,
      })
    ).isRequired,
    treatmentCasesTooltip: string,
  }).isRequired,
  // only qsr tables will have this value
  treatmentCases: oneOfType([string, bool]).isRequired,
  isTreatmentReason: bool,
  // object for qsr table with treatmentReason (other case = false)
  tooltip: oneOfType([
    bool,
    shape({
      title: string.isRequired,
      tip: string.isRequired,
    }),
  ]),
  description: oneOfType([bool, string]),
}

export default IndicatorTable
